import { motion } from "framer-motion";
import {
  AlertTriangle,
  BarChart2,
  MapPin,
  Vote,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import API from "../services/api";
import { useAuth } from "../context/AuthContext";

const MyConstituency = () => {
  const { isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    const fetchData = async () => {
      try {
        const res = await API.get("/dashboard/constituency");
        setData(res.data);
      } catch (err) {
        console.error("Constituency data error:", err);
        toast.error("Failed to load constituency data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authLoading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-100 via-white to-green-100">
        <div className="text-center animate-pulse">
          <div className="h-10 w-64 bg-gray-200 rounded-lg mx-auto"></div>
          <div className="h-20 w-80 bg-gray-300 mt-4 rounded-lg mx-auto"></div>
        </div>
      </div>
    );
  }

  const { mla, mp, budget, issues } = data || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-6xl mx-auto space-y-10">

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-xl shadow-md"
        >
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            🏛️ My Constituency
          </h1>

          <div className="text-gray-600 flex items-center gap-2">
            <MapPin size={18} />
            <span>
              {mla?.district || "District Name"}, {mla?.constituency || "Constituency"}
            </span>
          </div>
        </motion.div>

        {/* Leaders */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {[mla, mp].map((rep, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-xl shadow border-t-4 border-orange-400"
            >
              <h3 className="text-xl font-bold mb-2">
                {rep?.position || "Representative"}
              </h3>

              <p className="text-gray-700 font-semibold">
                {rep?.name || "Name"}
              </p>

              <p className="text-sm text-gray-500">
                {rep?.party || "Party"}
              </p>

              <p className="text-sm text-gray-500">
                Votes Received: {rep?.votes || "N/A"}
              </p>

              <p className="text-sm mt-1 text-blue-700 hover:underline cursor-pointer">
                Compare Performance →
              </p>
            </motion.div>
          ))}

        </div>

        {/* Budget */}
        <div className="bg-white p-6 rounded-xl shadow space-y-4">

          <h3 className="text-xl font-semibold flex items-center gap-2">
            <BarChart2 size={20} /> Budget Allocation
          </h3>

          <p className="text-sm text-gray-600">
            Current Year: ₹{budget?.total?.toLocaleString() || "N/A"}
          </p>

          <ul className="text-sm text-gray-700 list-disc list-inside">

            {(budget?.breakdown || []).map((b, i) => (
              <li key={i}>
                {b.sector}: ₹{b.amount?.toLocaleString()}
              </li>
            ))}

          </ul>

        </div>

        {/* Issues */}
        <div className="bg-white p-6 rounded-xl shadow space-y-4">

          <h3 className="text-xl font-semibold flex items-center gap-2">
            <AlertTriangle size={20} /> Public Issues in Your Area
          </h3>

          {issues?.length === 0 ? (
            <p className="text-sm text-gray-600">
              No issues reported yet.
            </p>
          ) : (
            <ul className="space-y-2">

              {issues.map((issue) => (
                <li
                  key={issue._id}
                  className="text-sm text-gray-700"
                >
                  <span className="font-medium">
                    {issue.title}
                  </span>{" "}
                  — {issue.status}
                </li>
              ))}

            </ul>
          )}

        </div>

        {/* Feedback */}
        <div className="bg-white p-6 rounded-xl shadow space-y-4">

          <h3 className="text-xl font-semibold flex items-center gap-2">
            <Vote size={20} /> Feedback & Accountability
          </h3>

          <div className="grid grid-cols-2 gap-4">

            {[mla, mp].map((rep, i) => (
              <div key={i} className="p-4 border rounded-lg">

                <p className="font-medium mb-2">
                  {rep?.position}: {rep?.name}
                </p>

                <div className="flex gap-2">

                  <button className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700">
                    👍 Support
                  </button>

                  <button className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600">
                    👎 Oppose
                  </button>

                </div>

              </div>
            ))}

          </div>

        </div>

        {/* Explore */}
        <div className="text-center mt-10">

          <Link
            to="/explore-candidates"
            className="text-sm text-orange-600 hover:underline"
          >
            👥 Explore More Candidates
          </Link>

        </div>

      </div>
    </div>
  );
};

export default MyConstituency;
